import { Search, Star, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import type { NavItem } from './buildNavItems';
import {
  agruparNavPorCategoria,
  buscarMenu,
  navItemParaBusca,
  type MenuBuscaItem,
  type NavGrupo,
} from './navCatalog';
import styles from './MegaMenu.module.css';

type MegaMenuProps = {
  open: boolean;
  onClose: () => void;
  items: NavItem[];
  /** Itens que so aparecem na busca (ex.: cada relatorio). Sem codigoModulo, nao sao favoritaveis. */
  itensBuscaExtras?: MenuBuscaItem[];
  isFavorito: (codigoModulo: number) => boolean;
  onToggleFavorito: (codigoModulo: number) => void;
};

export function MegaMenu({
  open,
  onClose,
  items,
  itensBuscaExtras = [],
  isFavorito,
  onToggleFavorito,
}: MegaMenuProps) {
  const navigate = useNavigate();
  const [consulta, setConsulta] = useState('');
  const [destaque, setDestaque] = useState(0);
  const conteudoRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const consultaRef = useRef(consulta);
  const consultaAnteriorRef = useRef(consulta);
  const destaqueRef = useRef(destaque);
  const resultadosRef = useRef<MenuBuscaItem[]>([]);

  const grupos: NavGrupo[] = useMemo(() => agruparNavPorCategoria(items), [items]);

  const catalogoBusca = useMemo(
    () => [...items.map(navItemParaBusca), ...itensBuscaExtras],
    [items, itensBuscaExtras],
  );

  const buscando = consulta.trim().length > 0;
  const resultados = useMemo(
    () => (buscando ? buscarMenu(catalogoBusca, consulta) : []),
    [buscando, catalogoBusca, consulta],
  );

  const consultaMudou = consultaAnteriorRef.current !== consulta;
  consultaAnteriorRef.current = consulta;
  consultaRef.current = consulta;
  resultadosRef.current = resultados;
  if (!consulta.trim() || consultaMudou || destaque >= resultados.length) {
    destaqueRef.current = 0;
  } else {
    destaqueRef.current = destaque;
  }

  useEffect(() => {
    setDestaque(0);
  }, [consulta]);

  useEffect(() => {
    if (!open) {
      setConsulta('');
      setDestaque(0);
      return;
    }

    document.body.style.overflow = 'hidden';
    inputRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (!consultaRef.current.trim()) return;
      const lista = resultadosRef.current;
      if (lista.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = Math.min(destaqueRef.current + 1, lista.length - 1);
        destaqueRef.current = next;
        setDestaque(next);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const next = Math.max(destaqueRef.current - 1, 0);
        destaqueRef.current = next;
        setDestaque(next);
      } else if (e.key === 'Enter') {
        const item = lista[destaqueRef.current];
        if (!item) return;
        e.preventDefault();
        navigate(item.path);
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, navigate]);

  useEffect(() => {
    if (!buscando) return;
    conteudoRef.current
      ?.querySelector(`[data-busca-index="${destaque}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [buscando, destaque]);

  if (!open) return null;

  const fecharSeCliqueSimples = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    onClose();
  };

  return createPortal(
    <div className={styles.overlay} onClick={onClose} role="presentation">
      <div
        ref={conteudoRef}
        className={styles.conteudo}
        role="dialog"
        aria-modal="true"
        aria-label="Menu de módulos"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.buscaRow}>
          <div className={styles.buscaWrap}>
            <Search size={18} className={styles.buscaIcon} aria-hidden />
            <span className={styles.buscaRotulo} id="mega-menu-busca-rotulo">
              O que você deseja fazer?
            </span>
            <input
              ref={inputRef}
              type="search"
              className={styles.busca}
              value={consulta}
              onChange={(e) => setConsulta(e.target.value)}
              aria-labelledby="mega-menu-busca-rotulo"
              aria-controls={buscando ? 'mega-menu-resultados' : undefined}
              aria-activedescendant={
                buscando && resultados[destaque] ? `mega-menu-opcao-${resultados[destaque].id}` : undefined
              }
              autoComplete="off"
            />
          </div>
          <button type="button" className={styles.fechar} onClick={onClose} aria-label="Fechar menu">
            <X size={20} />
          </button>
        </div>

        {buscando ? (
          resultados.length === 0 ? (
            <p className={styles.vazio}>Nenhum resultado encontrado.</p>
          ) : (
            <ul id="mega-menu-resultados" className={styles.resultados}>
              {resultados.map((item, index) => {
                const Icone = item.icon;
                const ativo = index === destaque;
                return (
                  <li key={item.id}>
                    <Link
                      id={`mega-menu-opcao-${item.id}`}
                      to={item.path}
                      data-busca-index={index}
                      className={`${styles.resultado} ${ativo ? styles.resultadoAtivo : ''}`}
                      onMouseEnter={() => setDestaque(index)}
                      onClick={fecharSeCliqueSimples}
                    >
                      <Icone size={22} className={styles.resultadoIcone} aria-hidden />
                      <span className={styles.resultadoTexto}>
                        <span className={styles.resultadoTitulo}>
                          {item.titulo}
                          {item.categoria ? <span className={styles.badge}>{item.categoria}</span> : null}
                        </span>
                        {item.descricao ? (
                          <span className={styles.resultadoDescricao}>{item.descricao}</span>
                        ) : null}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )
        ) : (
          <div className={styles.colunas}>
            {grupos.map(({ categoria, items: grupoItems }) => (
              <section key={categoria.codigo} className={styles.coluna}>
                <h2 className={styles.categoriaTitulo}>{categoria.rotulo.toUpperCase()}</h2>
                <ul className={styles.lista}>
                  {grupoItems.map((item) => {
                    const favorito = isFavorito(item.codigoModulo);
                    return (
                      <li key={item.path}>
                        <div className={styles.item}>
                          <button
                            type="button"
                            className={`${styles.estrela} ${favorito ? styles.estrelaAtiva : ''}`}
                            aria-label={
                              favorito
                                ? `Remover ${item.label} dos atalhos`
                                : `Adicionar ${item.label} aos atalhos`
                            }
                            aria-pressed={favorito}
                            onClick={() => onToggleFavorito(item.codigoModulo)}
                          >
                            <Star
                              size={16}
                              fill={favorito ? 'currentColor' : 'none'}
                              strokeWidth={favorito ? 0 : 2}
                            />
                          </button>
                          <Link to={item.path} className={styles.rotulo} onClick={fecharSeCliqueSimples}>
                            {item.label}
                            {item.badge != null && item.badge > 0
                              ? ` (${item.badge > 99 ? '99+' : item.badge})`
                              : ''}
                          </Link>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
